export const en = {
  // Top Banner
  banner: {
    text: "Introducing SafeClaw: A safer, enhanced version of OpenClaw.",
    date: "(Available Now)"
  },
  
  // Navbar
  nav: {
    capabilities: "Capabilities",
    applications: "Applications",
    platform: "Platform",
    contact: "Contact",
    aboutUs: "About Us",
    requestAccess: "Request Access",
    languageTooltip: "You can select language here / 这里可以选择语言"
  },
  
  // Hero Section
  hero: {
    eyebrow: "Enterprise Trustworthy AI Agents",
    title: "Building Trustworthy AI Agents for High-Trust Industries",
    description: "AgentecFusion is an enterprise agentic AI platform built with CUHK research, designed for regulated and data-sensitive industries. Deploy autonomous agents with evidence-anchored reasoning, federated learning, and full governance.",
    getEarlyAccess: "Get Early Access",
    contactSales: "Contact Sales",
    videoNotice: "Please wait for the video to load and enable sound (muted by default)",
    videoCaption: "AgentecFusion: Building Trustworthy AI Agents for High-Stakes Industries"
  },
  
  // Why AgentecFusion Section
  why: {
    title: "Why AgentecFusion?",
    subtitle: "Built on decades of research, designed for enterprise trust and control",
    features: [
      {
        title: "Trustworthy AI by Design",
        points: [
          "Evidence-anchored reasoning",
          "Reduced hallucination",
          "Explicit safety and governance layer"
        ]
      },
      {
        title: "Research-Driven with CUHK",
        points: [
          "30+ years of research foundation",
          "Federated learning and imprinting technologies",
          "Designed for highly regulated sectors"
        ]
      },
      {
        title: "Enterprise-Grade Control & Security",
        points: [
          "Compliance-ready",
          "Full audit and observability",
          "Role-based access, data protection"
        ]
      }
    ]
  },
  
  // Applications Section
  applications: {
    title: "Applications",
    subtitle: "AI agents tailored for high-stakes industries",
    cases: [
      {
        icon: "✈️",
        title: "Tourism",
        description: "Recommender systems let AI agents craft personalized travel experiences based on individual preferences and market trends, increasing satisfaction and booking conversions."
      },
      {
        icon: "🏦",
        title: "Finance",
        description: "Trustworthy AI enables agents to detect fraud, avoid data leakage, and ensure compliance—delivering a competitive edge in high-demand markets."
      },
      {
        icon: "🎓",
        title: "Education",
        description: "Web intelligence agents perform deep research over the internet, synthesizing information into customized, up-to-date learning modules that go beyond standard curricula."
      },
      {
        icon: "🎧",
        title: "Customer Service",
        description: "Emotion-aware and insight-driven agents handle repetitive inquiries, escalate edge cases, and provide 24/7 support with consistent quality."
      }
    ]
  },
  
  // Platform Architecture Section
  platform: {
    title: "AgentecFusion Platform Architecture",
    subtitle: "Built on three layers of research: Intelligence, Trustworthy, and Autonomous Agent.",
    intelligence: {
      title: "Intelligence Layer",
      subtitle: "Strong Basis of Our Research",
      recommender: {
        title: "Recommender System",
        items: ["Deep Learning", "Hybrid Architectures", "Context-Aware Models", "Reinforcement Learning", "Multimodal Recommendation"]
      },
      web: {
        title: "Web Intelligence",
        items: ["Data Analytics", "Knowledge Graphs", "Iterative Retrieval", "Third-party Plug-ins", "Graph Neural Networks"]
      },
      customer: {
        title: "Customer Service Intelligence",
        items: ["Emotion-Aware AI", "Continuous Learning", "Insight-Driven Service", "Multimodal & Personalized LLMs"]
      }
    },
    trustworthy: {
      title: "Trustworthy Layer",
      subtitle: "Ensuring Safe & Reliable AI",
      explainability: {
        title: "Explainability & Interpretability",
        items: ["XAI Framework", "Model Watermarking", "Open Policy Agent"]
      },
      robustness: {
        title: "Robustness & Reliability",
        items: ["AI Firewalls", "Formal Verification", "Adversarial Debiasing", "Model Drift Detection", "Data Lineage & Provenance"]
      },
      privacy: {
        title: "Privacy Preservation",
        items: ["Federated Learning", "Differential Privacy", "Zero-Knowledge Proof", "Homomorphic Encryption"]
      }
    },
    agent: {
      title: "Autonomous Agent Layer",
      subtitle: "Intelligent Autonomous Systems",
      core: {
        title: "Agent Core Engine",
        items: ["ReAct", "Planning", "Reflexion", "Memory Management", "Hierarchical Reasoning"]
      },
      tooling: {
        title: "Tooling & Environment Interface",
        items: ["Tool Integration", "Task Automation", "Exception Recovery", "Model Context Protocol"]
      },
      orchestration: {
        title: "Orchestration & Ops Platform",
        items: ["Workflow Orchestration", "Multi-Agent Collaboration", "Inter-Agent Communication", "Data Drift & Model Ops Monitoring"]
      }
    }
  },
  
  // Integration Hub Section
  integration: {
    title: "Integration Ecosystem",
    subtitle: "Connect AgentecFusion with your existing tools and workflows",
    hubTitle: "AI Orchestration Hub",
    customIntegration: "Don't see your app? Contact us for a custom integration.",
    categories: {
      communication: "Communication",
      crm: "CRM & Sales",
      ai: "AI & ML"
    }
  },
  
  // About Us Section
  about: {
    title: "About Us",
    team: {
      title: "A team built for trustworthy agentic AI",
      description: "Our current team harnesses the knowledge and skills of 40+ professionals and emerging talents.",
      detail: "We bring together researchers, engineers, and product builders with deep experience in AI platforms, education technology, and large-scale enterprise systems.",
      roles: {
        researchers: "Researchers",
        engineers: "Engineers",
        productOps: "Product & Ops"
      }
    },
    trackRecord: {
      title: "Proven track record in real deployments",
      achievements: [
        "Our CEO architected and implemented Lenovo's enterprise AI platform (LeapAI), enabling scalable, high-performance AI transformation across industries.",
        "We deployed VeriGuide, which has checked 6M+ documents for 200K+ users across 4,000+ institutions and organizations in AP, NA, and EU.",
        "Our platform KEEPCourse supports 150K+ users across 8 Hong Kong universities and 1,700+ courses, with 99.9% uptime and enterprise-grade security.",
        "We have secured HK$5.85M in tech grants through partnerships with top firms like AT&T, Microsoft, Huawei, Meitu, and Amazon.",
        "Our AI systems deliver proven results in real business environments, using technologies co-developed with these partners."
      ],
      products: "Previous Products / Team Experience",
      metrics: {
        users: "Total users registered",
        institutions: "Institutions connected"
      },
      partners: "Partners from industry"
    },
    industry: {
      title: "Deep industry experience",
      description: "Our team members previously worked at leading global companies, bringing experience from technology, finance, and telecommunications."
    }
  },
  
  // Contact Section
  contact: {
    title: "Ready to Transform Your Business?",
    subtitle: "Contact our team to discuss how AgentecFusion can empower your industry with AI",
    aiLead: {
      name: "Jinhu Qi",
      title: "AI Research Lead",
      subtitle: "Technical Inquiries & Research Collaboration"
    },
    vp: {
      name: "Qiudan Wang",
      title: "Business Manager",
      subtitle: "Business Partnerships & Investment"
    },
    wechat: "WeChat",
    linkedin: "LinkedIn",
    emailLabel: "Or email directly at"
  },
  
  // Footer
  footer: {
    tagline: "Enterprise Trustworthy AI Agents",
    description: "Building autonomous AI agents for high-trust industries with evidence-anchored reasoning and full governance.",
    company: {
      title: "Company",
      about: "About Us",
      careers: "Careers",
      contact: "Contact"
    },
    product: {
      title: "Product",
      platform: "Platform",
      capabilities: "Capabilities",
      applications: "Applications",
      pricing: "Pricing"
    },
    resources: {
      title: "Resources",
      documentation: "Documentation",
      blog: "Blog",
      caseStudies: "Case Studies",
      support: "Support"
    },
    legal: {
      title: "Legal",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      security: "Security"
    },
    copyright: "© 2025 AgentecFusion. All rights reserved.",
    builtWith: "Built with ❤️ for Enterprise AI"
  },
  
  // Privacy Policy
  privacy: {
    title: "Privacy Policy",
    lastUpdated: "Last Updated",
    section1: {
      title: "Information We Collect",
      content: "We collect information you provide directly to us, including name, email address, company information, and any other information you choose to provide when using our services."
    },
    section2: {
      title: "How We Use Your Information",
      content: "We use the information we collect to provide, maintain, and improve our services, to communicate with you, and to comply with legal obligations."
    },
    section3: {
      title: "Data Security",
      content: "We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction."
    },
    section4: {
      title: "Your Rights",
      content: "You have the right to access, correct, or delete your personal information. You may also object to or restrict certain processing of your data."
    },
    contact: {
      title: "Contact Us",
      content: "If you have any questions about this Privacy Policy, please contact us at:"
    }
  },
  
  // Terms of Service
  terms: {
    title: "Terms of Service",
    lastUpdated: "Last Updated",
    section1: {
      title: "Acceptance of Terms",
      content: "By accessing and using AgentecFusion services, you accept and agree to be bound by the terms and provision of this agreement."
    },
    section2: {
      title: "Use License",
      content: "Permission is granted to temporarily access AgentecFusion services for personal, non-commercial transitory viewing only."
    },
    section3: {
      title: "Service Availability",
      content: "We strive to provide continuous service availability but do not guarantee uninterrupted access. We reserve the right to modify or discontinue services at any time."
    },
    section4: {
      title: "Limitation of Liability",
      content: "AgentecFusion shall not be liable for any indirect, incidental, special, consequential or punitive damages resulting from your use of or inability to use the service."
    },
    contact: {
      title: "Contact Us",
      content: "For questions about these Terms of Service, please contact us at:"
    }
  },
  
  // SafeClaw Product Page
  safeclaw: {
    hero: {
      badge: "Now Available · v0.1.1",
      subtitle: "A Safer, Enhanced Alternative to OpenClaw",
      description: "Enterprise-grade AI agent platform co-developed with CUHK research team. Multi-layer security, local deployment, full data sovereignty — designed for privacy-sensitive scenarios.",
      download: "Download Now",
      learnMore: "Learn More",
      metaOpen: "Open Source",
      metaSafe: "Enterprise Security",
      metaCross: "Cross-Platform",
      terminalInit: "Initializing SafeClaw environment...",
      terminalSandbox: "WASM + Docker dual sandbox activated",
      terminalReady: "SafeClaw v0.1.1 ready",
      terminalStatus: "Status:",
      terminalRunning: "Running",
      terminalSecurity: "Security:",
      terminalMax: "Maximum"
    },
    features: {
      title: "Core Advantages",
      subtitle: "Enterprise-grade security architecture designed for high-trust scenarios",
      items: [
        {
          icon: "🛡️",
          title: "Enterprise Multi-Layer Protection",
          description: "Dual sandbox isolation with credential host boundary injection, ensuring maximum data security.",
          points: [
            "WASM + Docker dual sandbox isolation",
            "Credential host boundary injection",
            "Prompt injection detection",
            "Sensitive information leak prevention"
          ]
        },
        {
          icon: "🚀",
          title: "Simple & Intuitive",
          description: "Designed for local users, set up in 3 steps with no technical expertise required.",
          points: [
            "Graphical one-click deployment",
            "Feishu ecosystem support",
            "Built-in productivity skills",
            "Zero command-line barrier"
          ]
        },
        {
          icon: "🏗️",
          title: "Self-Developed & Controllable",
          description: "Core architecture built with Rust, supporting local deployment with full data sovereignty.",
          points: [
            "Rust core engine",
            "Local or cloud deployment",
            "Data stays within your control",
            "Multi-model access support"
          ]
        }
      ]
    },
    usecases: {
      title: "Use Cases",
      subtitle: "From workplace efficiency to smart living, SafeClaw empowers every scenario",
      categories: [
        {
          emoji: "💼",
          title: "Efficient Workplace Partner",
          items: [
            "Admin Assistant — Auto-organize schedules, generate daily reports",
            "Email Secretary — Smart email management and reply drafting",
            "Meeting Expert — Auto-record meetings, extract key points",
            "Dev Partner — Code assistance, debugging, and diagram generation",
            "Data Analyst — Excel processing, data analysis, and reporting",
            "Collaboration Hub — Multi-platform sync across WeChat, Feishu, DingTalk"
          ]
        },
        {
          emoji: "🏠",
          title: "Smart Life Assistant",
          items: [
            "Home Control — Unified smart home management",
            "AI Tutor — Patient homework guidance and explanation",
            "Travel Planner — Full itinerary planning and booking",
            "Fitness Coach — Custom training plans and reminders",
            "Finance Advisor — Market data analysis and investment tips",
            "Shopping Guru — Auto price comparison and product reviews"
          ]
        }
      ]
    },
    comparison: {
      title: "SafeClaw vs OpenClaw",
      subtitle: "See why SafeClaw is the better choice for security-focused users",
      header: {
        feature: "Feature"
      },
      rows: [
        { feature: "Security Architecture", safeclaw: "Enterprise multi-layer", openclaw: "Single layer", openclawStatus: "no" },
        { feature: "Sandbox Isolation", safeclaw: "WASM + Docker dual sandbox", openclaw: "No sandbox", openclawStatus: "no" },
        { feature: "Credential Protection", safeclaw: "Host boundary injection", openclaw: "Direct exposure risk", openclawStatus: "no" },
        { feature: "Data Localization", safeclaw: "Full local deployment", openclaw: "Cloud dependent", openclawStatus: "partial" },
        { feature: "Localized Interface", safeclaw: "Full Chinese GUI", openclaw: "English only", openclawStatus: "no" },
        { feature: "Ecosystem Integration", safeclaw: "Feishu, WeCom, DingTalk", openclaw: "Limited integrations", openclawStatus: "partial" },
        { feature: "Setup Complexity", safeclaw: "3-step quick start", openclaw: "Complex configuration", openclawStatus: "no" },
        { feature: "Compliance", safeclaw: "PIPL & DSL compliant", openclaw: "No local compliance", openclawStatus: "no" }
      ]
    },
    tech: {
      title: "Technical Architecture",
      subtitle: "Self-developed, controllable, and extensible",
      items: [
        {
          icon: "🔐",
          title: "Security Stack",
          points: [
            "WASM sandbox — Application-level isolation",
            "Docker sandbox — System-level isolation",
            "Credential host boundary injection",
            "Prompt injection & leak detection",
            "Pairing & allowlist access control"
          ]
        },
        {
          icon: "🔌",
          title: "Open Ecosystem",
          points: [
            "OpenAI-compatible protocol",
            "WASM tool plugins",
            "WASM channel adapters",
            "HTTP API for downstream integration",
            "Multi-model support (Ollama + Cloud)"
          ]
        },
        {
          icon: "☁️",
          title: "Flexible Deployment",
          points: [
            "Local deployment — Data stays in your network",
            "Hybrid deployment — Local + cloud collaboration",
            "Multi-model failover",
            "Routines + Heartbeat automation",
            "Web gateway with full logging"
          ]
        }
      ]
    },
    download: {
      title: "Download SafeClaw",
      subtitle: "Get started with SafeClaw in minutes — available for all major platforms",
      releaseDate: "Latest Release · April 2026",
      recommended: "Recommended for your system",
      downloadBtn: "Download",
      requirements: {
        title: "System Requirements",
        items: [
          "4-core CPU or above",
          "8GB RAM or above",
          "256GB storage recommended",
          "macOS 12+, Windows 10+, or Ubuntu 20.04+"
        ]
      }
    },
    pricing: {
      title: "Choose Your Plan",
      subtitle: "From personal exploration to enterprise deployment — a plan for every stage",
      popular: "Most Popular",
      plans: [
        {
          name: "Community",
          price: "¥0",
          period: "",
          originalPrice: null,
          featured: false,
          description: "Perfect for individuals and developers getting started with AI agents in a secure local environment.",
          features: [
            "1 secure agent instance",
            "Local deployment support",
            "WASM sandbox isolation",
            "Community support",
            "Pay-per-use token billing"
          ],
          cta: "Get Started Free"
        },
        {
          name: "Growth",
          price: "¥100",
          period: "/mo",
          originalPrice: null,
          featured: true,
          description: "Everything you need for productive AI workflows, with expanded capacity and premium token credits.",
          features: [
            "Up to 2 agent instances",
            "Included 13M tokens/mo",
            "Docker + WASM dual sandbox",
            "Feishu integration",
            "Token usage pooling",
            "Email support"
          ],
          cta: "Start Growth Plan"
        },
        {
          name: "Enterprise",
          price: "¥1,000",
          period: "/mo",
          originalPrice: null,
          featured: false,
          description: "Full-scale deployment with advanced security, high-volume tokens, and dedicated support for teams.",
          features: [
            "Up to 5 agent instances",
            "Included 130M tokens/mo",
            "Full security suite",
            "Multi-platform integration",
            "Priority support",
            "Custom skill packages"
          ],
          cta: "Contact Sales"
        }
      ]
    }
  }
};
